import './App.css';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BrowserRouter, NavLink, Navigate, Route, Routes } from 'react-router-dom';

import AuditCareType from './components/AuditCareType';
import AuditDepartments from './components/AuditDepartments';
import AuditIsReferable from './components/AuditIsReferable';
import DataEnvironmentSelector from './components/DataEnvironmentSelector';
import DirectScheduling from './components/DirectScheduling';
import EpicIdLookup from './components/EpicIdLookup';
import ExecutiveDashboard from './components/ExecutiveDashboard';
import OperationsGuide from './components/OperationsGuide';
import ProtectedRoute from './components/ProtectedRoute';
import ProviderDetail from './components/ProviderDetail';
import ProviderQueue from './components/ProviderQueue';
import ProviderSearch from './components/ProviderSearch';
import React from 'react';
import Settings from './components/Settings';
import UserMenu from './components/UserMenu';

/**
 * Nav items double as the route table so each page stays directly linkable.
 *
 * Ordered to match the working sequence the operations guide teaches: read the
 * dashboard, clear the department queue (one Epic change unblocks many), then
 * work individual providers. Lookup tools and the older audits follow, with the
 * guide and settings last as reference.
 */
const NAV_ITEMS: { to: string; label: string }[] = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/departments', label: 'Department Queue' },
  { to: '/provider-queue', label: 'Provider Queue' },
  { to: '/search', label: 'Provider Search' },
  { to: '/epic-lookup', label: 'Epic ID Lookup' },
  { to: '/audit-isreferable', label: 'Audit IsReferable' },
  { to: '/audit-caretype', label: 'Audit Care Types' },
  { to: '/guide', label: 'Guide' },
  { to: '/settings', label: 'Settings' }
];

function AppContent() {
  const { user, logout } = useAuth();

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <h1>Provider Validator</h1>
          <nav className="main-nav">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="header-actions">
          <DataEnvironmentSelector />
          <UserMenu name={user?.name} email={user?.email} roles={user?.roles} onLogout={logout} />
        </div>
      </header>

      <main className="app-main">
        <Routes>
          {/* Sign-in returns to the site root, so this is the landing view. */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<ExecutiveDashboard />} />
          <Route path="/search" element={<ProviderSearch />} />
          <Route path="/epic-lookup" element={<EpicIdLookup />} />
          <Route path="/epic-lookup/:epicId" element={<EpicIdLookup />} />
          <Route path="/audit-isreferable" element={<AuditIsReferable />} />
          <Route path="/audit-caretype" element={<AuditCareType />} />
          <Route path="/provider-queue" element={<ProviderQueue />} />
          {/* Digest emails sent before the rename still point at the old path. */}
          <Route path="/audit-epic-blocks" element={<Navigate to="/provider-queue" replace />} />
          <Route path="/guide" element={<OperationsGuide />} />
          <Route path="/departments" element={<AuditDepartments />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/provider/:npi/direct" element={<DirectScheduling />} />
          <Route path="/provider/:npi" element={<ProviderDetail />} />
          <Route path="/providers/:npi" element={<ProviderDetail />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ProtectedRoute>
          <AppContent />
        </ProtectedRoute>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
